import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface AddCommentRequest {
  content: string;
  isInternal?: boolean;
}

function extractWorkspaceId(workspaceObj: unknown): string {
  const w = workspaceObj as { id?: string; SK?: string } | null | undefined;
  if (!w) return '';
  if (w.id) return w.id;
  if (w.SK) {
    if (w.SK.startsWith('WS#')) return w.SK.substring(3);
    return w.SK;
  }
  return '';
}

async function addComment(
  ticketId: string,
  data: AddCommentRequest,
  token: string | undefined,
  workspaceId: string,
): Promise<unknown> {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.post(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/${ticketId}/comments`,
    {
      content: data.content,
      isInternal: data.isInternal ?? false,
      attachments: [],
    },
    { headers },
  );

  return response.data;
}

export function useAddComment() {
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();
  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: AddCommentRequest }) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return addComment(ticketId, data, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onSuccess: (_data, variables) => {
      trackEvent('ticket_comment_added', {
        ticket_id: variables.ticketId,
        is_internal: variables.data.isInternal ?? false,
      });
      queryClient.invalidateQueries([queryKeys.comments, variables.ticketId, workspaceId]);
      queryClient.invalidateQueries([queryKeys.tickets, workspaceId]);
      queryClient.invalidateQueries([queryKeys.ticketActivity, variables.ticketId, workspaceId]);
    },
  });
}
